/**
 * Shared helpers for the Expense Reports page. Amounts come from the service as
 * integer cents and are only turned into dollars for display.
 */
import { fmtCurrencyPrecise } from '../cashReserves/helpers';

// Must match ExpenseCategory in backend/service_expense_reports/models.py.
export const CATEGORIES = [
  'Meals',
  'Travel',
  'Lodging',
  'Mileage',
  'Supplies',
  'Software/Subscriptions',
  'Conference/Training',
  'Client Gifts',
  'Other',
];

// Must match ATTENDEE_CATEGORIES in the service's models.py.
export const ATTENDEE_CATEGORIES = ['Meals', 'Client Gifts'];

// The reimbursement policy's guideline for how soon an expense should be submitted.
export const LATE_AFTER_DAYS = 30;

export const STATUS_META = {
  pending: { label: 'Pending', badge: 'text-bg-warning' },
  approved: { label: 'Approved', badge: 'text-bg-primary' },
  rejected: { label: 'Rejected', badge: 'text-bg-danger' },
  paid: { label: 'Paid', badge: 'text-bg-success' },
};

export const WAIVER_LABELS = {
  not_ready: 'Not ready yet',
  special_case: 'Special case',
};

export const ACTION_LABELS = {
  receipt_added: 'Receipt added',
  receipt_removed: 'Receipt removed',
  receipt_waived: 'No receipt',
  submitted: 'Submitted',
  edited: 'Edited',
  approved: 'Approved',
  rejected: 'Rejected',
  paid: 'Marked paid',
  note: 'Note',
};

export const fmtCents = (cents) => (cents == null ? '—' : fmtCurrencyPrecise(cents / 100));

export const sumCents = (expenses) => expenses.reduce((total, e) => total + (e.amount_cents || 0), 0);

/** "2026-10-15" -> "Oct 15, 2026", without the UTC shift `new Date('2026-10-15')` causes. */
export const fmtExpenseDate = (isoDate) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate || '');
  if (!match) return isoDate || '';
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.toLocaleDateString(undefined, { dateStyle: 'medium' });
};

/** Today's date as YYYY-MM-DD in the viewer's time zone, for the date input. */
export const todayIso = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/** The most recent history entry carrying a note, or null. */
export const latestNote = (expense) => {
  const withNotes = (expense.history || []).filter((entry) => entry.note);
  return withNotes.length ? withNotes[withNotes.length - 1] : null;
};

/** A readable message from an axios error, including FastAPI's 422 detail lists. */
export const errorMessage = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail.map((d) => String(d.msg || '').replace(/^Value error, /, '')).filter(Boolean).join('; ');
  }
  return err?.message || 'Something went wrong';
};

/** $0.76, $0.725: two decimals unless the rate has a half cent. Rates are millidollars per mile. */
export const fmtRate = (millis) =>
  millis % 10 === 0 ? `$${(millis / 1000).toFixed(2)}` : `$${(millis / 1000).toFixed(3)}`;

/** The rate in effect on an ISO date, from the service's ascending rate list, or null. */
export const rateFor = (rates, isoDate) => {
  let current = null;
  (rates || []).forEach((rate) => {
    if (isoDate && isoDate >= rate.effective_from) current = rate;
  });
  return current;
};

/** miles x rate in cents, rounded half-up, exactly as the service prices it. */
export const mileageCents = (milesTenths, rateMillis) => Math.round((milesTenths * rateMillis) / 100);

export const fmtMiles = (milesTenths) => (milesTenths / 10).toLocaleString('en-US', { maximumFractionDigits: 1 });

/** "226 mi × $0.76/mi" for mileage priced at an IRS rate, else ''. */
export const fmtMileage = (expense) =>
  expense.miles_tenths != null && expense.mileage_rate_millis != null
    ? `${fmtMiles(expense.miles_tenths)} mi × ${fmtRate(expense.mileage_rate_millis)}/mi`
    : '';

/** The business purpose with the mileage breakdown and attendees, as one line. */
export const purposeLine = (expense) =>
  [expense.description, fmtMileage(expense), expense.attendees ? `Attendees: ${expense.attendees}` : '']
    .filter(Boolean)
    .join(' · ');

const dayNumber = (year, monthIndex, day) => Date.UTC(year, monthIndex, day) / 86400000;

/** Whole days from the expense date to the day it was submitted (in the viewer's time zone). */
export const daysToSubmit = (expense) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(expense.expense_date || '');
  const submitted = new Date(expense.submitted_at);
  if (!match || Number.isNaN(submitted.getTime())) return null;
  return (
    dayNumber(submitted.getFullYear(), submitted.getMonth(), submitted.getDate()) -
    dayNumber(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  );
};

/** Submitted more than 30 days after the expense date (informational only). */
export const isLate = (expense) => {
  const days = daysToSubmit(expense);
  return days !== null && days > LATE_AFTER_DAYS;
};

/** Receipts not removed or replaced. */
export const currentReceipts = (expense) => (expense.receipts || []).filter((r) => !r.removed_at);

/** Receipts that were removed or replaced (approvers only receive these). */
export const earlierReceipts = (expense) => (expense.receipts || []).filter((r) => r.removed_at);

/** "Not ready yet: paper receipt to follow", or '' when there's no reason on file. */
export const waiverText = (expense) =>
  expense.receipt_waiver
    ? `${WAIVER_LABELS[expense.receipt_waiver.reason] || expense.receipt_waiver.reason}: ${expense.receipt_waiver.explanation}`
    : '';

/** File names, or "No receipt (reason)" for reports and tooltips. */
export const receiptSummary = (expense) => {
  const files = currentReceipts(expense).map((r) => r.filename);
  if (files.length) return files.join(', ');
  const reason = waiverText(expense);
  return reason ? `No receipt (${reason})` : 'No receipt';
};
